import { Content, ContentStack, ContentTable } from "pdfmake/interfaces";
import { CompanyType } from "react-lib/apps/HISV3/common/CompanyLogoForm";
import { emptyHospitalContent, HospitalAddressMap, OADetailType } from "react-lib/apps/HISV3/common/HeaderPdfFormTemplate";
import HeaderSapiensForm from "react-lib/apps/HISV3/DPO/sequence/HeaderSapiensForm";
import formatComma from "react-lib/utils/formatComma";

import CONFIG from "config/config";

const color = {
  greyHeader: "#D9D9D9",
  greySummary: "#E8E8E8",
  greyBorder: "#9f9f9f",
  pink: "#F382A0",
  blue: "#095e9d",
};

export type FormServiceItemBreakdownType = {
  // data
  fields?: Record<string, any>[];
  oaDetail?: OADetailType;
  hn?: string;
  patientNameTH?: string;
  patientNameEN?: string;
  printedDate?: string;
  printedBy?: string;
  data?: any[];
  division?: string;
};

const extractHn = (hn: string): string => {
  if (!hn) return "";

  const parts = hn.split("_");

  return parts[parts.length - 1];
};

const FormServiceItemBreakdown = async (props: FormServiceItemBreakdownType) => {
  var origin = window?.location?.origin;

  const getHospitalAddress = (company: CompanyType, oaDetail?: OADetailType): Content => {
    return HospitalAddressMap[company]?.(oaDetail, props?.division || "") ?? emptyHospitalContent;
  };

  const headerSapiensForm = await HeaderSapiensForm({
    form: "FormServiceItemBreakdown",
    images: {
      oaLogo: props.oaDetail?.imageFile || "",
    },
    logoHight:
      CONFIG.COMPANY === "MSK"
        ? 40
        : CONFIG.COMPANY === "RAMASRI"
          ? 45
          : CONFIG.COMPANY === "MEHEALTH"
            ? 45
            : 24,
    pageMargins: [0, 158, 0, 25],
    hospitalAddress: getHospitalAddress(CONFIG.COMPANY as CompanyType, props.oaDetail),
    header: {
      margin: [-8, 0, 0, 0],
      stack: headerPaymentSummary(props),
    },
    styles: {
      paymentSummaryHeader: {
        bold: true,
        fontSize: 8,
      },
      paymentSummaryInfo: {
        fontSize: 8,
      },
    },
  });

  const fields = props.data ?? [];

  const fileData = fields.map((item: any, index: number) => {
    const showLine = index % 36 == 0;

    if (item.type === "date") {
      return [
        {
          columns: [
            {
              width: 110,
              text: "วันที่ค่าใช้จ่าย/ Expense Date:",
              bold: true,
            },
            {
              width: "*",
              text: item?.date || "",
            },
          ],
          font: "KanitLM",
          style: "tableData",
          margin: [5, 0, 0, 0],
          colSpan: 5,
        },
        {},
        {},
        {},
        {},
      ];
    } else if (item.type === "total") {
      return [
        {
          text: "",
          border: [true, false, true, true],
        },
        {
          text: "ยอดรวมเงิน/SubTotal",
          bold: true,
          font: "KanitLM",
          style: "tableData",
          margin: [5, 0, 0, 0],
          border: [true, false, true, true],
        },
        {
          text: "",
          border: [true, false, true, true],
        },
        {
          text: "",
          border: [true, false, true, true],
        },
        {
          text: formatComma(item?.total || ""),
          font: "KanitLM",
          style: "tableData",
          margin: [0, 0, 5, 0],
          alignment: "right",
          border: [true, false, true, true],
        },
      ];
    } else if (item.type === "outstandingBalance") {
      return [
        {
          text: "ยอดค้างชำระสุทธิ/ Outstanding Balance",
          bold: true,
          font: "KanitLM",
          style: "tableData",
          margin: [5, 0, 0, 0],
          border: [true, false, false, true],
          fillColor: color.greyHeader,
          colSpan: 2,
        },
        {},
        {
          text: "",
          border: [false, false, false, true],
          fillColor: color.greyHeader,
        },
        {
          text: "",
          border: [false, false, false, true],
          fillColor: color.greyHeader,
        },
        {
          text: formatComma(item?.total || ""),
          font: "KanitLM",
          style: "tableData",
          margin: [0, 0, 5, 0],
          alignment: "right",
          border: [false, false, true, true],
          fillColor: color.greyHeader,
        },
      ];
    } else {
      return [
        {
          text: CONFIG.COMPANY === "MEHEALTH" ? item?.pricing_code : item?.code,
          font: "KanitLM",
          style: "tableData",
          margin: [5, 0, 0, 0],
          border: [true, false, true, showLine],
        },
        {
          text: CONFIG.COMPANY === "MEHEALTH" ? item?.pricing_name : item?.display_name,
          font: "KanitLM",
          style: "tableData",
          margin: [5, 0, 0, 0],
          border: [true, false, true, showLine],
        },
        {
          text: item?.quantity || "",
          font: "KanitLM",
          style: "tableData",
          margin: [5, 0, 0, 0],
          alignment: "center",
          border: [true, false, true, showLine],
        },
        {
          text: formatComma(item?.unitprice || ""),
          font: "KanitLM",
          style: "tableData",
          margin: [0, 0, 5, 0],
          alignment: "right",
          border: [true, false, true, showLine],
        },
        {
          text: formatComma(item?.pay || ""),
          font: "KanitLM",
          style: "tableData",
          margin: [0, 0, 5, 0],
          alignment: "right",
          border: [true, false, true, showLine],
        },
      ];
    }
  });

  return {
    pageSize: "A4",
    ...headerSapiensForm,
    content: [
      {
        margin: [25, -11, 25, 0],
        stack: [
          {
            table: {
              headerRows: 1,
              widths: [55, "*", 40, 60, 60],
              body: [
                [
                  {
                    stack: [
                      {
                        text: "รหัส",
                        font: "KanitLM",
                        style: "paymentSummaryHeader",
                      },
                      {
                        text: "Code",
                        font: "PoppinsLM",
                        style: "paymentSummaryHeader",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                    border: [true, true, false, false],
                  },
                  {
                    stack: [
                      {
                        text: "รายการ",
                        font: "KanitLM",
                        style: "paymentSummaryHeader",
                      },
                      {
                        text: "Detail",
                        font: "PoppinsLM",
                        style: "paymentSummaryHeader",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                    border: [false, true, false, false],
                  },
                  {
                    stack: [
                      {
                        text: "จำนวน",
                        font: "KanitLM",
                        style: "paymentSummaryHeader",
                      },
                      {
                        text: "Qty",
                        font: "PoppinsLM",
                        style: "paymentSummaryHeader",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                    border: [false, true, false, false],
                  },
                  {
                    stack: [
                      {
                        text: "ราคา/หน่วย",
                        font: "KanitLM",
                        style: "paymentSummaryHeader",
                      },
                      {
                        text: "Price/Unit",
                        font: "PoppinsLM",
                        style: "paymentSummaryHeader",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                    border: [false, true, false, false],
                  },
                  {
                    stack: [
                      {
                        text: "ราคารวม",
                        font: "KanitLM",
                        style: "paymentSummaryHeader",
                      },
                      {
                        text: "Total",
                        font: "PoppinsLM",
                        style: "paymentSummaryHeader",
                      },
                    ],
                    alignment: "center",
                    fillColor: color.greyHeader,
                    border: [false, true, true, false],
                  },
                ],
                ...fileData,
              ],
            },
            layout: layoutTable,
          },
        ],
      },
    ],
    styles: {
      ...headerSapiensForm.styles,
      tableData: { fontSize: 8 },
      tableDataPoppins: { fontSize: 6 },
      tableHeaderSummary: {
        bold: true,
        fontSize: 8,
      },
      footerSignature: { fontSize: 9 },
      footerData: { bold: true, fontSize: 6 },
    },
    footer: function (currentPage: number, pageCount: number) {
      return {
        margin: [15, -7, 15, 0],
        stack: footerPaymentSummary(props),
      };
    },
    images: {
      ...headerSapiensForm.images,
      background: origin + "/static/images/logoform/SAPIENS/HeaderSapiensForm.png",
    },
  };
};

export default FormServiceItemBreakdown;

/*================================ HANDLES RECEIPT ==============================*/

const headerPaymentSummary = (props: FormServiceItemBreakdownType) => {
  return [
    {
      margin: [0, 0, 0, 15],
      stack: [
        {
          text: [
            {
              text: "รายการค่าบริการค้างชำระ",
              font: "KanitLM",
              style: {
                bold: true,
                fontSize: 10,
              },
            },
            {
              text: "(Service Item Breakdown)",
              font: "PoppinsLM",
              style: {
                bold: true,
                fontSize: 10,
              },
            },
          ],
          alignment: "center",
          border: [false, false, false, false],
        },
      ],
    },
    {
      table: {
        widths: [120, 130, 105, "*"],
        body: [
          [
            headerLabelCell("หมายเลขผู้รับบริการ ", "/ ID :"),
            headerText([textItem(extractHn(props.hn || ""), { font: "PoppinsLM" })]),
            headerLabelCell("ชื่อ ", "/ Name :"),
            headerText([textItem(`${props.patientNameTH} (${props.patientNameEN || ""})`)]),
          ],
        ],
      },
    },
  ];
};

const headerLabelCell = (th: string, en: string) => ({
  text: [
    { text: th, font: "KanitLM", style: "paymentSummaryHeader" },
    { text: en, font: "PoppinsLM", style: "paymentSummaryHeader" },
  ],
  alignment: "left",
  border: [false, false, false, false],
  margin: [20, 0, 0, 0],
});

type HeaderValueType = "text" | "columns";

const headerValueCell = (data: any, type: HeaderValueType = "text") => ({
  alignment: "left",
  border: [false, false, false, false],
  margin: [0, 0, 0, 0],
  ...(type === "columns" ? { columns: data } : { text: data ?? "" }),
});

type TextItemOptions = {
  font?: string;
  style?: string;
  width?: string | number;
  margin?: number[];
};

const textItem = (text: string, options: TextItemOptions = {}) => ({
  text,
  font: options.font ?? "KanitLM",
  style: options.style ?? "paymentSummaryInfo",
  ...(options.width ? { width: options.width } : {}),
  ...(options.margin ? { margin: options.margin } : {}),
});

const headerText = (text: any[]) => headerValueCell(text, "text");

/*================================ HOSPITAL ADDRESS ==============================*/


const bilingualTable = (thStack: any[], enStack: any[]): Content => ({
  relativePosition: { x: -10, y: 0 },
  table: {
    body: [
      [
        {
          lineHeight: 1,
          alignment: "left",
          border: [false, false, true, false],
          stack: thStack,
        },
        {
          lineHeight: 1.05,
          alignment: "left",
          margin: [5, 0, 0, 0],
          border: [true, false, false, false],
          stack: enStack,
        },
      ],
    ],
  },
});

/*================================ CONTENT RECEIPT ==============================*/

/*================================ FOOTER ==============================*/

const layoutTable = {
  hLineColor: () => color.greyBorder,
  vLineColor: () => color.greyBorder,
};

const thEn = (
  th: string,
  en: string,
  fontTh = "KanitLM",
  fontEn = "PoppinsLM",
  style = "tableHeaderSummary",
) => [
  { text: th, font: fontTh, style },
  { text: `/${en}`, font: fontEn, style },
];

const footerPaymentSummary = (props: FormServiceItemBreakdownType) => {
  return [
    {
      margin: [60, 10, 10, 10],
      columns: [
        {
          text: thEn("วันเวลาที่พิมพ์ ", "Printed Date :"),
          margin: [10, 0, 0, 0],
          width: 130,
        },
        {
          text: props.printedDate,
          font: "KanitLM",
          style: "tableData",
          margin: [0, 0, 3, 0],
          width: 130,
        },
        {
          text: thEn("ผู้พิมพ์ ", "Printed by :"),
          margin: [0, 0, 3, 0],
          width: 90,
        },
        {
          text: props.printedBy,
          font: "KanitLM",
          style: "tableData",
          margin: [0, 0, 3, 0],
          width: "*",
        },
      ],
    },
  ];
};
